import { FC, useState } from 'react'
import {
  Input,
  IconButton,
  Typography,
  Button,
  Spinner,
  Accordion,
  AccordionHeader,
  AccordionBody,
  Alert,
} from '@material-tailwind/react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import relativeTime from 'dayjs/plugin/relativeTime'
import { PiCheckBold, PiClipboard, PiSiren, PiTimer, PiWarningCircle } from 'react-icons/pi'
import { colors } from '@material-tailwind/react/types/generic'
import { AccordionCaret } from './AccordionCaret'
import { useFeatureAuth } from '../hooks/authorization'
import { FEATURES } from '../utils/constants/features.constants'
import { MAX_SIGNED_URL_VALIDITY, SIGNED_URL_VALIDITY } from '../utils/constants/supabase.constants'
import { useGeneralToast } from '../hooks/toast'
import { useAgamiMediaSignedUrlGenerate } from '../hooks/useAgamiMediaSignedUrlGenerate'
import { formatDateTimeString } from '../utils/helpers/dates.helpers'
import { useAgamiMediaSignedUrlDelete } from '../hooks/useAgamiMediaSignedUrlDelete'
import { useDarkMode } from '../contexts/DarkModeContext'
import { AgamiSpot } from '../types/supabase'

dayjs.extend(relativeTime)
dayjs.extend(duration)

interface AgamiPreGeneratedSignedUrlProps {
  agami: AgamiSpot
}

const AgamiPreGeneratedSignedUrl: FC<AgamiPreGeneratedSignedUrlProps> = ({ agami }) => {
  const { t } = useTranslation()
  const { darkMode } = useDarkMode()
  const toast = useGeneralToast()
  const [accordionOpen, setAccordionOpen] = useState(false)
  const experimentalFeatures = useFeatureAuth(FEATURES.EXPERIMENTAL_FEATURES)

  const [agamiSignedUrl, setAgamiSignedUrl] = useState<string | null>(agami?.audio_signed_url || null)
  const [agamiSignedUrlExpiration, setAgamiSignedUrlExpiration] = useState<string | null>(
    agami?.audio_signed_url_expires_at || null
  ) // ISO string

  const [expiresIn, setExpiresIn] = useState<number | null>(SIGNED_URL_VALIDITY)
  const expiresInValid = expiresIn !== null && Number.isInteger(expiresIn)
  const expired = agamiSignedUrlExpiration ? Date.now() > new Date(agamiSignedUrlExpiration).getTime() : false

  const [copied, setCopied] = useState(false)

  const copyUrlToClipboard = async () => {
    if (!agamiSignedUrl) return
    await navigator.clipboard.writeText(agamiSignedUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const { generateUrl, isLoading } = useAgamiMediaSignedUrlGenerate({
    agamiId: agami?.id,
    mediaType: agami?.content_media_type || 1,
    onSuccess: ({ signedUrl, signedUrlExpiresAt }: { signedUrl: string; signedUrlExpiresAt: string }) => {
      setAgamiSignedUrl(signedUrl)
      setAgamiSignedUrlExpiration(signedUrlExpiresAt)
      toast.success(t('signedUrl.messages.generatedSuccess'))
    },
    onError: () => toast.error(t('signedUrl.messages.generatedError')),
  })

  const { deleteUrl, isLoading: isDeleting } = useAgamiMediaSignedUrlDelete({
    agamiId: agami?.id,
    onSuccess: () => {
      toast.success(t('signedUrl.messages.deletedSuccess'))
      setAgamiSignedUrl(null)
      setAgamiSignedUrlExpiration(null)
    },
    onError: () => toast.error(t('signedUrl.messages.deletedError')),
  })

  return (
    <div className="bg-white dark:bg-primary rounded-lg p-4 shadow-sm">
      <Typography className="font-bold text-lg font-barlow">{t('signedUrl.title')}</Typography>
      <Accordion open={accordionOpen} icon={<AccordionCaret rotate={accordionOpen} />}>
        <AccordionHeader
          onClick={() => setAccordionOpen((prev) => !prev)}
          className="justify-center gap-4 text-md text-primary dark:text-base-gray-400"
        >
          {t('signedUrl.openSettings')}
        </AccordionHeader>

        <AccordionBody>
          {!experimentalFeatures && <>{t('signedUrl.featureNotAvailable')}</>}

          {experimentalFeatures && !agami && (
            <Alert variant="ghost" color="orange">
              {t('signedUrl.uploadFirst')}
            </Alert>
          )}

          {experimentalFeatures && agami && (
            <>
              <Typography className="mb-4">{t('signedUrl.description')}</Typography>

              {agamiSignedUrl && (
                <>
                  <Typography className="font-bold mb-2">{t('signedUrl.currentUrl')}</Typography>
                  <div className="relative flex w-full gap-1 justify-center items-center mb-1">
                    <Input
                      {...(darkMode && { color: 'white' })}
                      className="w-full"
                      value={agamiSignedUrl}
                      type="text"
                      label={t('signedUrl.signedUrl')}
                      readOnly
                      error={expired}
                    />

                    <IconButton
                      color={copied ? 'green' : ('primary' as colors)}
                      onClick={copyUrlToClipboard}
                      variant={copied ? 'text' : 'filled'}
                      disabled={copied || expired}
                    >
                      {copied ? <PiCheckBold className="h-6 w-6" /> : <PiClipboard className="h-4 w-4" />}
                    </IconButton>
                  </div>

                  {expired ? (
                    <Alert
                      color="red"
                      variant={darkMode ? 'outlined' : 'ghost'}
                      icon={<PiSiren className="w-6 h-6" />}
                    >
                      <Typography className="font-bold mb-2">{t('signedUrl.expiredMessage')}</Typography>

                      <Button size="sm" color="red" onClick={deleteUrl} disabled={isDeleting}>
                        {isDeleting ? <Spinner className="h-4 w-4" /> : t('signedUrl.deleteExpired')}
                      </Button>
                    </Alert>
                  ) : (
                    <Alert
                      color="green"
                      variant={darkMode ? 'outlined' : 'ghost'}
                      icon={<PiTimer className="w-6 h-6" />}
                    >
                      <span className="font-bold">{t('signedUrl.validUntil')}</span>{' '}
                      <em>
                        {agamiSignedUrlExpiration ? formatDateTimeString(agamiSignedUrlExpiration) : 'N/A'}
                      </em>
                    </Alert>
                  )}
                </>
              )}

              {(!agamiSignedUrl || expired) && (
                <div className="grid grid-cols-2 gap-x-2 mt-2">
                  <div className="col-span-2 mb-2">
                    <Typography className="font-bold">{t('signedUrl.generateNewUrl')}</Typography>
                  </div>

                  <Input
                    {...(darkMode && { color: 'white' })}
                    value={expiresIn ?? ''}
                    type="number"
                    inputMode="numeric"
                    label={t('signedUrl.expiresIn')}
                    max={MAX_SIGNED_URL_VALIDITY}
                    onChange={(e) => {
                      if (e.currentTarget.value === '') setExpiresIn(null)
                      else setExpiresIn(Number(e.currentTarget.value))
                    }}
                    error={!expiresInValid}
                  />

                  <Button
                    disabled={!expiresInValid || isLoading}
                    size="sm"
                    color="orange"
                    className="flex items-center justify-center"
                    onClick={() =>
                      generateUrl({
                        expiresIn: expiresIn || SIGNED_URL_VALIDITY,
                        mediaPath: agami?.audio_content_path,
                      })
                    }
                  >
                    {isLoading ? <Spinner className="h-4 w-4" /> : t('signedUrl.generate')}
                  </Button>

                  <div className="col-span-2">
                    {!expiresInValid && (
                      <Typography variant="small" color="red">
                        {t('signedUrl.insertNumericValue')}
                      </Typography>
                    )}
                    {expiresIn && expiresIn <= MAX_SIGNED_URL_VALIDITY && (
                      <Typography variant="small">
                        {dayjs.duration(expiresIn, 'seconds').humanize()}
                      </Typography>
                    )}
                    {expiresIn !== null && expiresIn > MAX_SIGNED_URL_VALIDITY && (
                      <Typography variant="small" color="red">
                        {t('signedUrl.maxAllowedValue', { max: MAX_SIGNED_URL_VALIDITY })}
                      </Typography>
                    )}
                  </div>
                </div>
              )}

              <Alert
                className="mt-4"
                variant={darkMode ? 'outlined' : 'ghost'}
                color="orange"
                icon={<PiWarningCircle className="w-6 h-6" />}
              >
                <ul className="ml-2 list-inside list-disc text-sm">
                  <li>{t('signedUrl.warnings.signedUrl')}</li>
                  <li>{t('signedUrl.warnings.cannotBeRevoked')}</li>
                  <li>{t('signedUrl.warnings.dynamicGeneration')}</li>
                  <li>{t('signedUrl.warnings.expiredIgnored')}</li>
                  <li>{t('signedUrl.warnings.useCarefully')}</li>
                </ul>
              </Alert>
            </>
          )}
        </AccordionBody>
      </Accordion>
    </div>
  )
}

export default AgamiPreGeneratedSignedUrl
